import React, { useEffect, useState } from 'react';
import { useMultilingualSettings } from '../../hooks/useMultilingualSettings';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useSidebar } from '../../hooks/useSidebar';
import { Globe } from 'lucide-react';
import { LanguageSettings } from '../../components/settings/multilingual/LanguageSettings';
import { TranslationModal } from '../../components/settings/multilingual/TranslationModal';
import { Language, TranslatedContent } from '../../types/multilingualSettings';

export default function MultilingualSettings() {
  const { chatbotPublicId } = useChatbot();
  const { 
    settings, 
    updateSettings, 
    toggleLanguage, 
    setDefaultLanguage, 
    addLanguage, 
    removeLanguage,
    initialized,
    initializeSettings
  } = useMultilingualSettings();
  
  const [editingLanguage, setEditingLanguage] = useState<Language | null>(null);
  const isMobile = useMediaQuery('(max-width: 1024px)');
  const { isOpen, setIsOpen } = useSidebar();

  useEffect(() => {
    if (chatbotPublicId && !initialized) {
      initializeSettings(chatbotPublicId);
    }
  }, [chatbotPublicId, initialized, initializeSettings]);

  // Auto-collapse sidebar on mobile
  useEffect(() => {
    if (isMobile && isOpen) {
      setIsOpen(false);
    }
  }, [isMobile, isOpen, setIsOpen]);

  const handleToggleEnabled = async () => {
    if (!chatbotPublicId) return;
    await updateSettings({ enabled: !settings.enabled }, chatbotPublicId);
  };

  const handleToggleLanguage = async (code: string) => {
    if (!chatbotPublicId) return;
    await toggleLanguage(code, chatbotPublicId);
  };

  const handleSetDefaultLanguage = async (code: string) => {
    if (!chatbotPublicId) return;
    await setDefaultLanguage(code, chatbotPublicId);
  };

  const handleAddLanguage = async (language: Language) => {
    if (!chatbotPublicId) return;
    await addLanguage(language, chatbotPublicId);
  };

  const handleRemoveLanguage = async (code: string) => {
    if (!chatbotPublicId) return;
    await removeLanguage(code, chatbotPublicId);
  };

  const handleSaveTranslations = async (translations: TranslatedContent) => {
    if (chatbotPublicId && editingLanguage) {
      const updatedSettings = {
        ...settings,
        translations: {
          ...settings.translations,
          [editingLanguage.code]: translations
        }
      };
      await updateSettings(updatedSettings, chatbotPublicId);
      setEditingLanguage(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">
          <Globe className="inline-block mr-3 text-indigo-400" />
          Multilingual Settings
        </h2>
        <p className="text-gray-400">Configure and manage languages for your chatbot</p>
      </div>

      <LanguageSettings 
        settings={settings}
        onToggleEnabled={handleToggleEnabled}
        onToggleLanguage={handleToggleLanguage}
        onSetDefaultLanguage={handleSetDefaultLanguage}
        onAddLanguage={handleAddLanguage}
        onRemoveLanguage={handleRemoveLanguage}
        onEditLanguage={setEditingLanguage}
      />

      {editingLanguage && (
        <TranslationModal 
          isOpen={!!editingLanguage} 
          onClose={() => setEditingLanguage(null)}
          language={editingLanguage}
          translations={settings.translations[editingLanguage.code]}
          onSave={handleSaveTranslations}
        />
      )}
    </div>
  );
}